import { Page } from '../widget/WidgetIllustrations';
import shared from '../widget/WidgetIllustrations.module.css';
import styles from './CaptureIllustrations.module.css';

/**
 * Drawings for How capture works (Jason, 10 Oct): the two ways the extension
 * captures, what a capture brings with it, and how it finds its project. They
 * share the widget drawings' page and colours, so the settings read as one
 * set. Decoration only, hidden from screen readers, since the text beside
 * each says the same; each rests on its finished state for anyone who asks
 * for less motion.
 */

/** Two ways to capture: the whole page as it is, or one point on it. */
export function CaptureWaysArt() {
  return (
    <svg className={shared.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid="capture-ways-art">
      <Page width={160} height={100} header={false} />
      <rect className={shared.ink} x={10} y={16} width={46} height={5} rx={2.5} />
      <rect className={shared.faint} x={10} y={26} width={64} height={3} rx={1.5} />
      <rect className={shared.faint} x={10} y={32} width={56} height={3} rx={1.5} />
      <rect className={shared.block} x={10} y={40} width={64} height={38} rx={3} />
      <rect className={shared.blockStrong} x={90} y={20} width={56} height={14} rx={3} />
      <rect className={shared.faint} x={90} y={42} width={60} height={3} rx={1.5} />
      <rect className={shared.faint} x={90} y={48} width={50} height={3} rx={1.5} />
      <rect className={shared.block} x={90} y={56} width={60} height={22} rx={3} />

      {/* The whole page: framed, snapped, and Alt+C to do it. */}
      <g className={styles.wholePage}>
        <path className={styles.bracket} d="M6 16 V6 H16 M144 6 H154 V16 M154 84 V94 H144 M16 94 H6 V84" />
        <rect className={styles.flash} x={2} y={2} width={156} height={96} rx={6} />
      </g>
      <g className={styles.keys}>
        <rect className={shared.key} x={10} y={83} width={17} height={10} rx={2} />
        <text className={shared.keyText} x={18.5} y={90.2} textAnchor="middle">Alt</text>
        <rect className={shared.key} x={30} y={83} width={11} height={10} rx={2} />
        <text className={shared.keyText} x={35.5} y={90.2} textAnchor="middle">C</text>
      </g>

      {/* One point: the element outlined, and a pin on it. */}
      <g className={styles.onePoint}>
        <rect className={shared.outline} x={87} y={17} width={62} height={20} rx={4} />
        <circle className={shared.accent} cx={148} cy={18} r={6} />
        <text className={shared.pinText} x={148} y={20.6} textAnchor="middle">1</text>
      </g>
    </svg>
  );
}

/**
 * What comes with it: a ticket holding the screenshot, the console, the
 * network requests and the errors, with the secrets blacked out before any of
 * it leaves the browser.
 */
export function CaptureContentsArt() {
  return (
    <svg className={shared.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid="capture-contents-art">
      <rect className={shared.panel} x={4} y={4} width={152} height={92} rx={5} />
      {/* The screenshot. */}
      <g className={styles.revealOne}>
        <rect className={shared.block} x={12} y={12} width={54} height={36} rx={3} />
        <rect className={shared.ink} x={17} y={18} width={22} height={3} rx={1.5} />
        <rect className={shared.faint} x={17} y={25} width={40} height={2.5} rx={1.25} />
        <rect className={shared.faint} x={17} y={30} width={34} height={2.5} rx={1.25} />
        <rect className={shared.faint} x={17} y={36} width={24} height={7} rx={1.5} />
      </g>
      {/* The console. */}
      <g className={styles.revealTwo}>
        <text className={styles.prompt} x={74} y={18.5}>&gt;_</text>
        <rect className={shared.faint} x={86} y={14.5} width={54} height={3} rx={1.5} />
        <rect className={shared.faint} x={86} y={20} width={42} height={3} rx={1.5} />
      </g>
      {/* The network requests, as a waterfall. */}
      <g className={styles.revealThree}>
        <rect className={styles.bar} x={74} y={28} width={22} height={3.5} rx={1.75} />
        <rect className={styles.bar} x={84} y={33.5} width={34} height={3.5} rx={1.75} />
        <rect className={styles.bar} x={96} y={39} width={28} height={3.5} rx={1.75} />
      </g>
      {/* An error. */}
      <g className={styles.revealFour}>
        <circle className={styles.errorDot} cx={77} cy={48} r={2.5} />
        <rect className={shared.faint} x={83} y={46.5} width={50} height={3} rx={1.5} />
      </g>
      <line className={shared.rule} x1={12} x2={148} y1={58} y2={58} />
      {/* Secrets blacked out in the browser, before anything is sent. */}
      <rect className={shared.ink} x={12} y={65} width={38} height={3.5} rx={1.75} />
      <rect className={`${styles.redacted} ${styles.blackOut}`} x={56} y={63} width={80} height={7.5} rx={2} />
      <rect className={shared.ink} x={12} y={79} width={24} height={3.5} rx={1.75} />
      <rect className={`${styles.redacted} ${styles.blackOutLate}`} x={42} y={77} width={94} height={7.5} rx={2} />
    </svg>
  );
}

/**
 * How a capture reaches this project: the page's address matches one of the
 * project's URL patterns, and the ticket lands on its board.
 */
export function CaptureRouteArt() {
  return (
    <svg className={shared.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid="capture-route-art">
      {/* The page, and its address. */}
      <rect className={shared.page} x={2} y={12} width={72} height={76} rx={5} />
      <path className={shared.bar} d="M2 24 V17 A5 5 0 0 1 7 12 H69 A5 5 0 0 1 74 17 V24 Z" />
      <rect className={styles.address} x={8} y={14.5} width={60} height={7} rx={3.5} />
      <rect className={styles.matched} x={12} y={17} width={26} height={2} rx={1} />
      <rect className={shared.faint} x={40} y={17} width={22} height={2} rx={1} />
      <rect className={shared.ink} x={8} y={32} width={34} height={4} rx={2} />
      <rect className={shared.faint} x={8} y={40} width={56} height={3} rx={1.5} />
      <rect className={shared.block} x={8} y={48} width={58} height={32} rx={3} />
      {/* It matches one of the project's patterns, so across it goes. */}
      <g className={styles.patternMatch}>
        <circle className={shared.accent} cx={86} cy={40} r={4.5} />
        <path className={styles.checkMark} d="M83.8 40.2 l1.6 1.6 l3 -3.2" />
      </g>
      <path className={shared.trail} d="M78 50 H94" />
      <path className={styles.arrowHead} d="M91 46.5 L95 50 L91 53.5" />

      {/* The project's board, the new ticket landing in its first column. */}
      <rect className={shared.page} x={98} y={12} width={60} height={76} rx={5} />
      <rect className={shared.ink} x={104} y={18} width={26} height={4} rx={2} />
      <rect className={shared.block} x={104} y={28} width={15} height={54} rx={2} />
      <rect className={shared.block} x={122} y={28} width={15} height={54} rx={2} />
      <rect className={shared.block} x={140} y={28} width={13} height={54} rx={2} />
      <rect className={styles.card} x={124} y={32} width={11} height={9} rx={1.5} />
      <rect className={styles.card} x={142} y={32} width={9} height={9} rx={1.5} />
      <g className={styles.dropIn}>
        <rect className={styles.newCard} x={106} y={32} width={11} height={11} rx={1.5} />
        <rect className={shared.onAccent} x={108} y={35} width={7} height={1.6} rx={0.8} />
        <rect className={shared.onAccent} x={108} y={38.5} width={5} height={1.6} rx={0.8} />
      </g>
    </svg>
  );
}

export type RecordingModeChoice = 'own' | 'video' | 'audio';

/** A person, head and shoulders, centred on x with the head at y. */
function Person({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <circle className={styles.person} cx={x} cy={y} r={7} />
      <path className={styles.person} d={`M${x - 13} ${y + 22} a13 11 0 0 1 26 0 z`} />
    </g>
  );
}

/** A microphone centred on (x, y), with its sound coming off it. */
function Voice({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect className={styles.micBody} x={x - 3.5} y={y - 9} width={7} height={12} rx={3.5} />
      <path className={styles.micStand} d={`M${x - 6.5} ${y - 1} a6.5 6.5 0 0 0 13 0 M${x} ${y + 5.5} v3.5 M${x - 3.5} ${y + 9} h7`} />
      <g className={styles.sound}>
        <path className={styles.soundWave} d={`M${x + 10} ${y - 6} q3 5 0 10 M${x + 14} ${y - 9} q5 8 0 16`} />
      </g>
    </g>
  );
}

/**
 * What a walkthrough of this project records, for Walkthrough recording
 * (Jason, 10 Oct): whatever each person chose in their own extension, the
 * screen with their voice, or their voice alone with the pins' screenshots.
 */
export function RecordingModeArt({ mode }: { mode: RecordingModeChoice }) {
  return (
    <svg className={shared.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid={`recording-mode-art-${mode}`}>
      {mode === 'own' && (
        <>
          {/* Two people, each with the choice in their own extension. */}
          <Person x={32} y={22} />
          <rect className={styles.badge} x={38} y={40} width={18} height={13} rx={3} />
          <path className={shared.accent} d="M45 43.5 l5.5 3 l-5.5 3 z" />
          <Person x={86} y={22} />
          <rect className={styles.badge} x={92} y={40} width={18} height={13} rx={3} />
          <rect className={styles.micBody} x={99.2} y={42} width={3.6} height={6.5} rx={1.8} />
          <path className={styles.micStand} d="M97.6 46.5 a3.4 3.4 0 0 0 6.8 0 M101 50 v1.5" />
        </>
      )}
      {mode === 'video' && (
        <>
          {/* The window, recording, and the voice over it. */}
          <Page width={84} height={64} />
          <rect className={shared.ink} x={8} y={30} width={34} height={4} rx={2} />
          <rect className={shared.faint} x={8} y={38} width={50} height={3} rx={1.5} />
          <rect className={shared.block} x={8} y={45} width={30} height={13} rx={2.5} />
          <rect className={shared.recBadge} x={50} y={44} width={28} height={11} rx={5.5} />
          <circle className={shared.recDot} cx={56} cy={49.5} r={2.6} />
          <text className={shared.recText} x={61} y={52}>REC</text>
          <Voice x={100} y={34} />
        </>
      )}
      {mode === 'audio' && (
        <>
          {/* The voice, as it is recorded, and a pin's screenshot beside it. */}
          <g className={styles.levels}>
            {[14, 24, 10, 30, 18, 26, 12, 22, 16].map((height, index) => (
              <rect
                key={index}
                className={styles.level}
                style={{ animationDelay: `${index * 0.12}s` }}
                x={8 + index * 8}
                y={36 - height / 2}
                width={4}
                height={height}
                rx={2}
              />
            ))}
          </g>
          <Voice x={92} y={22} />
          <rect className={shared.block} x={84} y={44} width={30} height={20} rx={2.5} />
          <rect className={shared.ink} x={88} y={48} width={14} height={2.5} rx={1.25} />
          <rect className={shared.faint} x={88} y={53} width={20} height={2.5} rx={1.25} />
          <circle className={shared.accent} cx={112} cy={45} r={4.5} />
          <text className={shared.pinText} x={112} y={47.4} textAnchor="middle" style={{ fontSize: 6 }}>1</text>
        </>
      )}
    </svg>
  );
}
